import { AlertTriangle, Calendar } from "lucide-react";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

// Timeline types
export interface TimelineItem {
  id: string;
  name: string;
  subtitle?: string;
  color: string;
  data: TimelineData[];
}

export interface TimelineData {
  date: DateTime;
  shift?: TimelineShift;
  isHoliday?: boolean;
  isEmpty?: boolean;
}

export interface TimelineShift {
  id: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  color: string;
  label?: string;
  subtitle?: string;
  isException?: boolean;
  onClick?: () => void;
}

export interface TimelineColumn {
  date: DateTime;
  label: string;
  isToday?: boolean;
  isPast?: boolean;
}

export interface TimelineProps {
  items: TimelineItem[];
  columns: TimelineColumn[];
  workingHoursStart?: number; // Default 6 (6AM)
  workingHoursEnd?: number; // Default 20 (8PM)
  showTimeScale?: boolean;
  showCurrentTime?: boolean;
  showHourMarkers?: boolean;
  className?: string;
  onItemClick?: (itemId: string, date: DateTime) => void;
  onEmptyClick?: (date: DateTime) => void;
  renderItemLabel?: (item: TimelineItem) => ReactNode;
  renderEmptyState?: (date: DateTime) => ReactNode;
}

const DEFAULT_WORKING_HOURS_START = 6;
const DEFAULT_WORKING_HOURS_END = 20;

export function Timeline({
  items,
  columns,
  workingHoursStart = DEFAULT_WORKING_HOURS_START,
  workingHoursEnd = DEFAULT_WORKING_HOURS_END,
  showTimeScale = true,
  showCurrentTime = true,
  showHourMarkers = true,
  className = "",
  onItemClick,
  onEmptyClick,
  renderItemLabel,
  renderEmptyState,
}: TimelineProps) {
  const totalWorkingHours = workingHoursEnd - workingHoursStart;

  const getTimePositionPercent = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const totalMinutes = (hours - workingHoursStart) * 60 + minutes;
    const workingDayMinutes = totalWorkingHours * 60;
    const percent = (totalMinutes / workingDayMinutes) * 100;
    return Math.max(0, Math.min(percent, 100));
  };

  const getShiftWidthPercent = (startTime: string, endTime: string) => {
    const startPercent = getTimePositionPercent(startTime);
    const endPercent = getTimePositionPercent(endTime);
    return Math.max(endPercent - startPercent, 8); // Minimum 8% width
  };

  const getCurrentTimePercent = () => {
    const now = DateTime.now();
    const currentTime = `${now.hour.toString().padStart(2, "0")}:${now.minute.toString().padStart(2, "0")}`;
    return getTimePositionPercent(currentTime);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const dateTime = DateTime.fromObject({
      hour: Number.parseInt(hours, 10),
      minute: Number.parseInt(minutes, 10),
    });
    return dateTime.toFormat(minutes === "00" ? "h a" : "h:mm a");
  };

  const getHourMarkers = () => {
    const markers: number[] = [];
    for (let hour = workingHoursStart + 1; hour < workingHoursEnd; hour += 3) {
      markers.push(hour);
    }
    return markers;
  };

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="space-y-4 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Column Headers */}
            <div className="flex items-center">
              <div className="w-32 flex-shrink-0 text-xs font-medium text-muted-foreground">
                {renderItemLabel ? "Items" : "Timeline"}
              </div>
              <div className="relative flex-1 h-12 overflow-hidden">
                <div
                  className="grid h-full"
                  style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.label}
                      className="flex flex-col items-center justify-center text-xs text-muted-foreground border-r border-border/30 last:border-r-0"
                    >
                      <div
                        className={`font-medium text-sm ${column.isToday ? "text-blue-600" : ""}`}
                      >
                        {column.label}
                      </div>
                      {column.date.isValid && (
                        <>
                          <div
                            className={`text-lg font-bold ${column.isToday ? "text-blue-600" : ""}`}
                          >
                            {column.date.toFormat("d")}
                          </div>
                          {column.isToday && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              Today
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline Items */}
            {items.map((item) => (
              <div key={item.id} className="flex items-center">
                {/* Item Label */}
                <div className="w-32 flex-shrink-0 pr-4">
                  {renderItemLabel ? (
                    renderItemLabel(item)
                  ) : (
                    <>
                      <div className="text-sm font-medium truncate">{item.name}</div>
                      {item.subtitle && (
                        <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                      )}
                      <div
                        className="w-3 h-3 rounded-full mt-1"
                        style={{ backgroundColor: item.color }}
                      />
                    </>
                  )}
                </div>

                {/* Timeline */}
                <div className="relative flex-1 h-16 bg-muted/20 rounded overflow-hidden">
                  <div
                    className="grid h-full"
                    style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
                  >
                    {item.data.map((dataPoint) => {
                      const column =
                        columns.find((c) => c.date.toISO() === dataPoint.date.toISO()) ??
                        columns[0];

                      return (
                        <div
                          key={dataPoint.date.toISO()}
                          className="relative border-r border-border/30 last:border-r-0"
                        >
                          {/* Hour markers */}
                          {showHourMarkers && (
                            <div className="absolute inset-0 pointer-events-none">
                              {getHourMarkers().map((hour) => {
                                const hourPercent = getTimePositionPercent(`${hour}:00`);
                                return (
                                  <div
                                    key={hour}
                                    className="absolute top-0 bottom-0 w-px bg-border/20"
                                    style={{ left: `${hourPercent}%` }}
                                  />
                                );
                              })}
                            </div>
                          )}

                          {dataPoint.isHoliday ? (
                            // Holiday indicator
                            <div className="absolute inset-1 bg-gray-200 dark:bg-gray-800 rounded flex items-center justify-center opacity-60">
                              <Calendar className="w-4 h-4 text-gray-400" />
                            </div>
                          ) : dataPoint.shift ? (
                            // Show shift with time-based positioning
                            <button
                              type="button"
                              className={`absolute top-2 bottom-2 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                dataPoint.shift.isException ? "ring-1 ring-orange-400" : ""
                              }`}
                              style={{
                                left: `${getTimePositionPercent(dataPoint.shift.startTime)}%`,
                                width: `${getShiftWidthPercent(dataPoint.shift.startTime, dataPoint.shift.endTime)}%`,
                                backgroundColor: dataPoint.shift.color,
                                opacity: 0.8,
                              }}
                              onClick={() => {
                                dataPoint.shift?.onClick?.();
                                onItemClick?.(item.id, column.date);
                              }}
                            >
                              <div className="p-1 text-xs text-white font-medium overflow-hidden h-full flex flex-col justify-center">
                                <div className="truncate">
                                  {dataPoint.shift.label ||
                                    `${formatTime(dataPoint.shift.startTime)}-${formatTime(dataPoint.shift.endTime)}`}
                                </div>
                                {dataPoint.shift.subtitle && (
                                  <div className="text-xs opacity-75 truncate">
                                    {dataPoint.shift.subtitle}
                                  </div>
                                )}
                              </div>

                              {dataPoint.shift.isException && (
                                <div className="absolute -top-1 -right-1">
                                  <AlertTriangle className="w-2 h-2 text-orange-600 bg-white rounded-full" />
                                </div>
                              )}
                            </button>
                          ) : dataPoint.isEmpty ? (
                            // Empty state
                            renderEmptyState ? (
                              renderEmptyState(column.date)
                            ) : (
                              <button
                                type="button"
                                className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-muted/50 rounded text-xs text-muted-foreground"
                                onClick={() => onEmptyClick?.(column.date)}
                              >
                                +
                              </button>
                            )
                          ) : null}

                          {/* Current Time Line for today */}
                          {showCurrentTime && column.isToday && (
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
                              style={{ left: `${getCurrentTimePercent()}%` }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}

            {/* Time Scale Legend */}
            {showTimeScale && (
              <div className="flex items-center">
                <div className="w-32 flex-shrink-0" />
                <div className="flex-1">
                  <div
                    className="grid"
                    style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
                  >
                    {columns.map((column) => (
                      <div
                        key={column.label}
                        className="text-center border-r border-border/30 last:border-r-0 py-2"
                      >
                        <div className="flex justify-between text-xs text-muted-foreground px-2">
                          <span>{workingHoursStart}AM</span>
                          <span>12PM</span>
                          <span>
                            {workingHoursEnd > 12
                              ? `${workingHoursEnd - 12}PM`
                              : `${workingHoursEnd}AM`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
